import type { Presentation } from "@/api/queries";

/**
 * The "Show as" form's state and its two translations (TILE-1): a tile's
 * stored spec into a draft, and a draft into the API's presentation.
 */

export interface Draft {
  type: string;
  x: string;
  y: string;
  series_by: string;
  size: string;
  label: string;
  geo: string;
  location: string;
  lat: string;
  lon: string;
  highlight: string;
  error: string;
  control: string;
  trendline: boolean;
  label_outliers: string;
  band_from: string;
  band_to: string;
  band_label: string;
  line_at: string;
  line_label: string;
}

export const EMPTY_DRAFT: Draft = {
  type: "table",
  x: "",
  y: "",
  series_by: "",
  size: "",
  label: "",
  geo: "",
  location: "",
  lat: "",
  lon: "",
  highlight: "",
  error: "",
  control: "",
  trendline: false,
  label_outliers: "",
  band_from: "",
  band_to: "",
  band_label: "",
  line_at: "",
  line_label: "",
};

function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

/** A tile's stored spec, as the form's starting point. */
export function draftFromSpec(spec: Record<string, unknown> | null | undefined): Draft {
  const source = spec ?? {};
  const annotations = Array.isArray(source.annotations)
    ? (source.annotations as Array<Record<string, unknown>>)
    : [];
  const band = annotations.find((entry) => entry.type === "band");
  const line = annotations.find((entry) => entry.type === "line" && entry.y !== undefined);
  const control = source.control;
  return {
    ...EMPTY_DRAFT,
    type: text(source.type) || "table",
    x: text(source.x),
    y: text(source.y),
    series_by: text(source.series_by),
    size: text(source.size),
    label: text(source.label),
    geo: text(source.geo),
    location: text(source.location),
    lat: text(source.lat),
    lon: text(source.lon),
    highlight: Array.isArray(source.highlight)
      ? (source.highlight as unknown[]).map(text).join(", ")
      : text(source.highlight),
    error: text(source.error),
    control:
      control && typeof control === "object"
        ? text((control as Record<string, unknown>).column)
        : text(control),
    trendline: Boolean(source.trendline),
    label_outliers: text(source.label_outliers),
    band_from: text(band?.from),
    band_to: text(band?.to),
    band_label: text(band?.label),
    line_at: text(line?.y),
    line_label: text(line?.label),
  };
}

/** The form, as the API's presentation: empty fields left out. */
export function presentationFrom(draft: Draft): Presentation {
  const out: Record<string, unknown> = { type: draft.type };
  for (const key of [
    "x",
    "y",
    "series_by",
    "size",
    "label",
    "geo",
    "location",
    "lat",
    "lon",
    "error",
    "control",
  ] as const) {
    if (draft[key].trim()) out[key] = draft[key].trim();
  }
  const highlight = draft.highlight
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  if (highlight.length) out.highlight = highlight;
  if (draft.trendline) out.trendline = true;
  const outliers = Number.parseInt(draft.label_outliers, 10);
  if (Number.isFinite(outliers) && outliers > 0) out.label_outliers = Math.min(10, outliers);
  const annotations: Array<Record<string, unknown>> = [];
  if (draft.band_from.trim() && draft.band_to.trim()) {
    annotations.push({
      type: "band",
      from: draft.band_from.trim(),
      to: draft.band_to.trim(),
      label: draft.band_label.trim(),
    });
  }
  const at = Number(draft.line_at);
  if (draft.line_at.trim() && Number.isFinite(at)) {
    annotations.push({ type: "line", y: at, label: draft.line_label.trim() });
  }
  if (annotations.length) out.annotations = annotations;
  return out as Presentation;
}
